import React, { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import classnames from 'classnames'
import styles from '../CourseLearningPathPage/CourseLearningPathPage.module.scss'
import LearningItemImage from '../../components/LearningItemImage/LearningItemImage'
import { useTranslation } from 'react-i18next'
import FavoriteBadge from '../../components/FavoriteBadge/FavoriteBadge'
import { CourseLearningPathQuickFacts } from './CourseLearningPathQuickFacts'
import { LearningItem, LearningItemAsPath } from '../../models/LearningItem'
import Markdown from 'react-markdown'
import { LearningItemStatus } from '../../models/BaseModels'
import { userCanCommentOnRoot, userCanEditLearningItem } from '../../state/calculations/permissions/userCan'
import Button from '../../components/basics/Button/Button'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import ThreadsDisplay from '../../components/comments/ThreadsDisplay/ThreadsDisplay'
import CoursePageAssets from '../CoursePage/CoursePageAssets'
import CourseLearningPathPageEnrolledUsers from './CourseLearningPathPageEnrolledUsers'
import CourseLearningPathLearningItems from './CourseLearningPathLearningItems'
import useAddUsersToLearningItems from '../../state/mutations/useAddUsersToLearningItems'
import { isUserAssignedToLearningItem } from '../../state/calculations/learningItemUserAssignments'
import CourseActivity from '../CoursePage/CourseActivity'

export const CourseLearningPathPage = React.memo(function CourseLearningPathPage({
  learningItemAsPath,
  learningItem,
}: {
  learningItemAsPath: LearningItemAsPath
  learningItem: LearningItem
}) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { uuid } = useParams()
  const { user: currentUser } = useSession()
  const addUsersToLearningItems = useAddUsersToLearningItems()

  const handlePathBooking = useCallback(async () => {
    await addUsersToLearningItems.mutateAsync({
      userUuids: [currentUser!.uuid],
      learningItemsAssignments: [
        {
          uuid: learningItemAsPath.uuid,
          dueDate: undefined,
        },
      ],
      skipNotifications: true,
    })
  }, [learningItemAsPath.uuid, currentUser?.uuid])

  const isAssigned =
    currentUser &&
    useMemo(() => isUserAssignedToLearningItem(currentUser.uuid, learningItem), [currentUser.uuid, learningItem])

  return (
    <>
      <article className={classnames(styles.CourseLearningPathPage, { skeleton: learningItem.isDummy })}>
        <header>
          <h1>{learningItem.title}</h1>
        </header>

        <main>
          <section className={styles.courseDescription}>
            <LearningItemImage learningItem={learningItem} full className={styles.coverImage} />

            {learningItem.status !== 'published' && (
              <span className={styles.status}>
                <span>{t(`learning item.status.${learningItem.status}`)}</span>
              </span>
            )}

            <span className={styles.courseType}>{t(`learning item.type.${learningItem.type}`)}</span>

            <FavoriteBadge course={learningItem} className={styles.favoriteBadge} />

            <div className={styles.belowImage}>
              {learningItemAsPath && <CourseLearningPathQuickFacts learningItemAsPath={learningItemAsPath} />}

              <div className={styles.actionsAndDescription}>
                <div className={styles.actions}>
                  {isAssigned ? (
                    <Button filled disabled={true}>
                      {t('learning item.booked')}
                    </Button>
                  ) : (
                    learningItem.status === LearningItemStatus.Published && (
                      <Button filled onClick={handlePathBooking}>
                        {t('learning item.book')}
                      </Button>
                    )
                  )}

                  {userCanEditLearningItem(currentUser, learningItem) && (
                    <Button component={Link} to={routes.editCourse.url({ uuid: uuid!, kind: 'learningPath' })}>
                      {t('generic cta.edit')}
                    </Button>
                  )}

                  <div>
                    <CourseActivity learningItem={learningItem} />
                  </div>
                </div>

                <div className={styles.descriptionText}>
                  <Markdown>{learningItem.description}</Markdown>
                </div>
              </div>
            </div>
          </section>

          <CoursePageAssets learningItemUuid={learningItem.uuid} />

          {userCanCommentOnRoot(currentUser, learningItem) && (
            <ThreadsDisplay learningItemUuid={learningItem.uuid} root={learningItem} />
          )}

          {learningItemAsPath && <CourseLearningPathLearningItems learningItemAsPath={learningItemAsPath} />}

          {learningItemAsPath && <CourseLearningPathPageEnrolledUsers learningItemAsPath={learningItemAsPath} />}
        </main>
      </article>
    </>
  )
})
